import React, {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react';
import cx from 'classnames';
import {NodeKey, SELECTION_CHANGE_COMMAND} from 'lexical';
import {$getSelectionStyleValueForProperty} from '@lexical/selection';
import {PriorityIcon, ToolbarIcons} from '@priority-software/priority-style-react';
import {$generateNodesFromDOM} from '@lexical/html';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
    $createTextNode,
    $getRoot,
    $getSelection,
    $isRangeSelection,
    $isTextNode,
    CAN_REDO_COMMAND,
    CAN_UNDO_COMMAND
} from 'lexical';
import {$isLinkNode, toggleLink} from '@lexical/link';
import {$patchStyleText} from '@lexical/selection';
import {$getNearestNodeOfType, mergeRegister} from '@lexical/utils';
import {$isListNode, ListNode} from '@lexical/list';
import useModal from '../../hooks/useModal';
import {SavePlugin} from '../../../../../../../components/LexicalEditor/plugins/SavePlugin/SavePlugin';
import {getSelectedNode} from '../../../../../../../components/LexicalEditor/utils/getSelectedNode';
import {
    COMMAND_PRIORITY_LOW,
    defaultStyles,
    FONT_SIZE
} from '../../../../../../../components/LexicalEditor/constants';
import {ShortCodesPlugin} from '../../../../../../../components/LexicalEditor/plugins/ShortCodesPlugin';
import {
    ADD_LINK_COMMAND,
    EditLinkPayload,
    INSERT_COMMENT,
    INSERT_TAB_SPACES,
    OPEN_LINK_MODAL_COMMAND,
    SET_TEXT_COLOR
} from '../../../../../../../components/LexicalEditor/commands';
import {formsStore} from '../../../../../../../stores/Form/FormsStore';
import {prepareSignature} from '../../utils/prepareSignature';
import {texts} from '../../texts';
import {LinkDialog} from '../../../../../../../components/LexicalEditor/components/Toolbar.parts/LinkDialog/LinkDialog';
import {getSelectionPoints} from '../../../../../../../utils/lexical/getSelectionPoints';
import {IconWithPopover} from '../../../../../../../components/LexicalEditor/components/Toolbar.parts/IconWithPopover';
import {HTML_FORM_TOOLBAR_CSS_VARS} from './constants';
import {EmailWritingAssistantPlugin} from '../../../../../../../components/LexicalEditor/plugins/EmailWritingAssistantPlugin';
import {BasicToolbar} from '../../../../../../../components/LexicalEditor/components/Toolbar.parts/Toolbar';
import {useFormatList} from '../../../../../../../components/LexicalEditor/hooks/useFormatList';
import {useCustomFontFormatCommand} from '../../../../../../../components/LexicalEditor/hooks/useCustomFontFormatCommand';
import {useCustomClipboardCommands} from '../../../../../../../components/LexicalEditor/hooks/useCustomClipboardCommands';
import {InsertImageDialog} from '../../../../../../../components/LexicalEditor/plugins/ImagesPlugin/components';
import {UpdateStatus} from '../../../../ScreenHeader/UpdateStatus';
import styles from './style.module.scss';
import {supportAIBlock} from '../../../../../../Automations/utils';

const Divider = () => <div className="priority-editor__toolbar-divider" />;

type ToolbarProps = {
    onSave: () => void;
    formID?: string;
    rtl?: boolean;
    toggleAILoading?: (isLoading: boolean) => void;
    showSuccessToast?: (message: string) => void;
    disabled?: boolean;
    session: string;
    saveDisabled?: boolean;
};

export function ToolbarPlugin({
    onSave,
    formID,
    rtl,
    disabled,
    session,
    toggleAILoading,
    showSuccessToast,
    saveDisabled
}: ToolbarProps) {
    const toolbarIdSuffix = useId();
    const toolbarID = `html-form-editor-toolbar${toolbarIdSuffix}`;
    const supportAI = useMemo(() => supportAIBlock(), []);

    const [editor] = useLexicalComposerContext();
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [isLink, setIsLink] = useState(false);

    const [modal, showModal] = useModal();
    const [linkModal, showLinkNodal] = useModal();
    const [, setSelectedElementKey] = useState<NodeKey>('');

    const lastSelectedColor = useRef('#000000');
    const {formatBulletList, formatNumberedList, setBlockType} = useFormatList({editor});
    const {handlePaste, handleCopyAndCut} = useCustomClipboardCommands({
        editor,
        portalSelector: formID
    });
    const {fontFormat, handleFontSizeChange, setFontFormat} = useCustomFontFormatCommand({editor});
    const {isStrikethrough, isItalic, isUnderline, isBold} = fontFormat;

    const updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            const element =
                anchorNode.getKey() === 'root'
                    ? anchorNode
                    : anchorNode.getTopLevelElementOrThrow();
            const elementKey = element.getKey();
            const elementDOM = editor.getElementByKey(elementKey);

            if (elementDOM !== null) {
                setSelectedElementKey(elementKey);
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType(anchorNode, ListNode);
                    const type = parentList ? parentList.getTag() : element.getTag();
                    setBlockType(type);
                } else {
                    const type = element.getType();
                    setBlockType(type);
                }
            }
            // Update text format
            setFontFormat({
                fontSize: $getSelectionStyleValueForProperty(
                    selection,
                    FONT_SIZE,
                    defaultStyles.fontSize + 'px'
                ),
                isBold: selection.hasFormat('bold'),
                isItalic: selection.hasFormat('italic'),
                isUnderline: selection.hasFormat('underline'),
                isStrikethrough: selection.hasFormat('strikethrough')
            });

            // Update links
            const node = getSelectedNode(selection);
            const parent = node.getParent();
            if ($isLinkNode(parent) || $isLinkNode(node)) {
                setIsLink(true);
            } else {
                setIsLink(false);
            }
        }
    }, [editor]);

    useEffect(() => {
        const root = editor.getRootElement();
        const handle = e => {
            const href = e.target.parentElement.href;

            if (href) window.open(href, '_blank').focus();
        };
        root?.addEventListener('dblclick', handle);
        return () => root?.removeEventListener('dblclick', handle);
    }, [editor]);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({editorState}) => {
                editorState.read(() => updateToolbar());
            }),

            editor.registerCommand(
                ADD_LINK_COMMAND,
                (payload: EditLinkPayload) => {
                    const selection = $getSelection();

                    if ($isRangeSelection(selection)) {
                        // add a new link text
                        const linkContent = payload.text || payload.url;
                        const [start, end] = getSelectionPoints(selection);
                        const selectionNewEnd = start + linkContent.length;
                        const node = selection.anchor.getNode();

                        if (
                            !payload.update &&
                            selection.focus.key === selection.anchor.key &&
                            start === end
                        ) {
                            // New link creation without selection
                            selection.insertText(linkContent);
                            node.select(start, selectionNewEnd);
                            toggleLink(payload.url, payload);
                        } else {
                            // Updating existing link data or selected text data
                            if ($isTextNode(node)) {
                                const deleteCoords: [number, number] =
                                    start === end
                                        ? [0, node.getTextContent().length]
                                        : [start, end - start];
                                node.spliceText(...deleteCoords, linkContent);
                            } else {
                                node.getChildren().length;
                                node.splice(0, node.getChildren().length, [
                                    $createTextNode(linkContent)
                                ]);
                            }
                            node.select(start, selectionNewEnd);
                            toggleLink(payload.url, payload);
                        }
                    }
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),

            editor.registerCommand(
                OPEN_LINK_MODAL_COMMAND,
                () => {
                    const selection = $getSelection();

                    if ($isRangeSelection(selection)) {
                        const node = getSelectedNode(selection);
                        const parent = node.getParent();
                        const linkUpdate = $isLinkNode(parent) || $isLinkNode(node);

                        let defaultURL = '';
                        let defaultText = '';

                        if ($isLinkNode(parent)) {
                            defaultURL = parent.getURL();
                            defaultText = parent.getTextContent();
                        } else if ($isLinkNode(node)) {
                            defaultURL = node.getURL();
                            defaultText = node.getTextContent();
                        } else if (selection.focus.offset !== selection.anchor.offset) {
                            defaultText = selection.getTextContent();
                        }

                        showLinkNodal(
                            linkUpdate ? texts.edit_link_dialog_title : texts.add_link_dialog_title,
                            onClose => (
                                <LinkDialog
                                    defaultUrl={defaultURL}
                                    defaultText={defaultText}
                                    update={linkUpdate}
                                    onClose={onClose}
                                />
                            )
                        );
                    }

                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),

            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                _payload => {
                    updateToolbar();
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(
                CAN_UNDO_COMMAND,
                payload => {
                    setCanUndo(payload);
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(
                CAN_REDO_COMMAND,
                payload => {
                    setCanRedo(payload);
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(
                SET_TEXT_COLOR,
                (payload?: Record<string, string>) => {
                    const selection = $getSelection();
                    if ($isRangeSelection(selection)) {
                        const styles = payload || {color: lastSelectedColor.current};
                        lastSelectedColor.current = styles.color;
                        $patchStyleText(selection, styles);
                    }
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(
                INSERT_COMMENT,
                () => {
                    editor?.update(() => {
                        const signatureData = formsStore.htmlSignature;
                        const htmlUpdateConstant = formsStore.sysConstants?.htmlupdate;
                        const shouldAddAtTop = htmlUpdateConstant?.Boolean === true;

                        const parser = new DOMParser();
                        const dom = parser.parseFromString(
                            prepareSignature(signatureData, !shouldAddAtTop),
                            'text/html'
                        );
                        const signatureNodes = $generateNodesFromDOM(editor, dom);
                        const cursorPositionNode = signatureNodes[1];
                        const children = $getRoot().getChildren();

                        if (shouldAddAtTop) {
                            // Add comment at the top
                            $getRoot().append(...signatureNodes, ...children);
                        } else {
                            // Add comment at the bottom
                            $getRoot().append(...children, ...signatureNodes);
                        }

                        if (cursorPositionNode) {
                            // set cursor position to an empty node under the tag with Date info
                            cursorPositionNode.selectStart();
                        }
                    });
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand(
                INSERT_TAB_SPACES,
                () => {
                    editor?.update(() => {
                        const selection = $getSelection();
                        if (selection) {
                            selection.insertText('   ');
                        }
                    });
                    return false;
                },
                COMMAND_PRIORITY_LOW
            )
        );
    }, [editor, updateToolbar]);

    const addComment = useCallback(() => {
        editor.dispatchCommand(INSERT_COMMENT, undefined);
    }, [editor]);

    const addLink = useCallback(() => {
        editor.dispatchCommand(OPEN_LINK_MODAL_COMMAND, null);
    }, [editor]);

    const insertImage = useCallback(() => {
        showModal(texts.img_dialog_title, onClose => (
            <InsertImageDialog activeEditor={editor} onClose={onClose} />
        ));
    }, [editor]);

    const onFontColorSelect = useCallback((color?: string) => {
        return editor.dispatchCommand(SET_TEXT_COLOR, color ? {color} : undefined);
    }, []);

    return (
        <BasicToolbar
            style={HTML_FORM_TOOLBAR_CSS_VARS}
            canUndo={canUndo}
            canRedo={canRedo}
            toolbarId={toolbarID}
            onFontColorSelect={onFontColorSelect}
            isBold={isBold}
            isItalic={isItalic}
            isUnderline={isUnderline}
            isStrikethrough={isStrikethrough}
            formatBulletList={formatBulletList}
            formatNumberedList={formatNumberedList}
            lastSelectedColor={lastSelectedColor.current}
            handleFontSizeChange={handleFontSizeChange}
            handleCopyAndCut={handleCopyAndCut}
            handlePaste={handlePaste}
            showClipboardButtons={false}
            emojiPickerPortalID={''} // pass empty string, to inject picker into the page body element
            disabled={disabled}
        >
            <IconWithPopover
                type="icon"
                icon={PriorityIcon.AddComment as any} // the current icon is not available in the toolbar icons set
                onClick={addComment}
                content={texts.comment_btn_title}
                disabled={disabled}
            />

            <Divider />

            <IconWithPopover
                type="icon"
                icon={ToolbarIcons.toolbarImage}
                onClick={insertImage}
                content={texts.picture_btn_title}
                disabled={disabled}
            />

            <IconWithPopover
                type="icon"
                icon={ToolbarIcons.toolbarAddLink}
                onClick={addLink}
                componentClasses={cx({active: isLink})}
                content={texts.hyperlink_btn_title}
                disabled={disabled}
            />

            <Divider />

            <ShortCodesPlugin
                formatBulletList={formatBulletList}
                formatNumberedList={formatNumberedList}
                insertImage={insertImage}
                disabled={disabled}
            />

            {supportAI ? (
                <EmailWritingAssistantPlugin
                    selectionMode={false}
                    portalId={formID}
                    language={rtl ? 'he' : 'en'}
                    toggleAILoading={toggleAILoading}
                    disabled={disabled}
                    showSuccessToast={showSuccessToast}
                />
            ) : null}

            <SavePlugin
                onSave={onSave}
                tooltipText={texts.save_btn_tooltip}
                label={texts.save_btn_title}
                disabled={saveDisabled}
            />

            {modal}
            {linkModal}
            <UpdateStatus
                session={session}
                supportHtmlForm={true}
                componentClasses={styles.saveNotifications}
            />
        </BasicToolbar>
    );
}
